import { Minus, Plus } from 'lucide-react'
import { cn } from '@/lib/utils'

interface NumberStepperProps {
  value: string
  onChange: (value: string) => void
  onBlur?: () => void
  min?: number
  max?: number
  step?: number
  disabled?: boolean
  placeholder?: string
  hasError?: boolean
  className?: string
  id?: string
  autoFocus?: boolean
}

function decimalPlaces(step: number): number {
  const s = String(step)
  const i = s.indexOf('.')
  return i === -1 ? 0 : s.length - i - 1
}

/** A quantity input with decrement/increment buttons flanking a centered number field.
 * Use for discrete counts (line-item quantities, stock quantities, duration) — not for
 * typed values like prices, rates, or measurements, which should stay plain number inputs. */
export function NumberStepper({
  value, onChange, onBlur, min, max, step = 1, disabled, placeholder, hasError, className, id, autoFocus,
}: NumberStepperProps) {
  const current = parseFloat(value)

  const bump = (delta: number) => {
    const base = Number.isFinite(current) ? current : (min ?? 0)
    const raw = base + delta
    // Clamp to the boundary's own precision rather than step's — step may be coarser
    // than min/max (e.g. step=1 with min=0.01), and rounding a clamped value to step's
    // decimal places would silently violate the boundary.
    if (min !== undefined && raw < min) { onChange(String(min)); return }
    if (max !== undefined && raw > max) { onChange(String(max)); return }
    onChange(raw.toFixed(decimalPlaces(step)))
  }

  const decrementDisabled = disabled || (min !== undefined && Number.isFinite(current) && current <= min)
  const incrementDisabled = disabled || (max !== undefined && Number.isFinite(current) && current >= max)

  return (
    <div
      className={cn(
        'inline-flex items-stretch w-full border rounded-md bg-surface overflow-hidden',
        'transition-colors duration-200 focus-within:ring-2 focus-within:ring-accent focus-within:border-accent',
        hasError ? 'border-danger-border' : 'border-border-input',
        disabled && 'opacity-60',
        className,
      )}
    >
      <button
        type="button"
        onClick={() => bump(-step)}
        disabled={decrementDisabled}
        aria-label="Decrease"
        className="flex items-center justify-center w-8 shrink-0 bg-transparent border-0 border-r border-border-input text-text-2 cursor-pointer hover:bg-surface-hover hover:text-text disabled:opacity-40 disabled:cursor-default disabled:hover:bg-transparent transition-colors duration-150"
      >
        <Minus size={14} strokeWidth={2} />
      </button>
      <input
        id={id}
        type="number"
        inputMode="decimal"
        value={value}
        onChange={e => onChange(e.target.value)}
        onBlur={onBlur}
        min={min}
        max={max}
        step={step}
        placeholder={placeholder}
        disabled={disabled}
        autoFocus={autoFocus}
        className="w-full min-w-0 flex-1 px-1 py-[0.45rem] text-center text-[0.9rem] bg-transparent text-text outline-none border-0"
      />
      <button
        type="button"
        onClick={() => bump(step)}
        disabled={incrementDisabled}
        aria-label="Increase"
        className="flex items-center justify-center w-8 shrink-0 bg-transparent border-0 border-l border-border-input text-text-2 cursor-pointer hover:bg-surface-hover hover:text-text disabled:opacity-40 disabled:cursor-default disabled:hover:bg-transparent transition-colors duration-150"
      >
        <Plus size={14} strokeWidth={2} />
      </button>
    </div>
  )
}
